import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
} from '@simplewebauthn/browser';

/**
 * Passkey UI switch: login button, post-login nudge, and Personal Settings enrollment.
 * Creation fails on iOS browsers other than Safari — they expose PublicKeyCredential and report a
 * platform authenticator, but only Safari can run the create ceremony on iOS.
 * Off while that is investigated; flip to true to bring the whole passkey UI back.
 * Server routes and stored credentials are untouched, so re-enabling needs no migration.
 */
export const PASSKEY_UI_ENABLED = true;

/** Cookie (and sessionStorage fallback) key: 'pending' after password login, 'skip' after passkey login */
const PASSKEY_NUDGE_LOGIN_FLAG_KEY = 'ic_passkey_nudge';

/** Enrolled cookie: ~1 year (this browser completed or used a passkey) */
const PASSKEY_NUDGE_ENROLLED_COOKIE_MAX_AGE_SEC = 365 * 24 * 60 * 60;

/** Explicit Not now snooze — Google/GitHub-style later, not forever */
const PASSKEY_NUDGE_DISMISS_SNOOZE_SEC = 30 * 24 * 60 * 60;

/** Login-hop pending/skip cookie: long enough to survive WebView remounts until Not now / enroll */
const PASSKEY_NUDGE_PENDING_COOKIE_MAX_AGE_SEC = 7 * 24 * 60 * 60;

/**
 * @param {string} name
 * @return {string|null}
 */
function getCookie(name) {
  try {
    const prefix = `${encodeURIComponent(name)}=`;
    const parts = String(document.cookie || '').split(';');
    for (let i = 0; i < parts.length; i += 1) {
      const part = parts[i].trim();
      if (part.indexOf(prefix) === 0) {
        return decodeURIComponent(part.slice(prefix.length));
      }
    }
  } catch (_e) {
    return null;
  }
  return null;
}

/**
 * @param {string} name
 * @param {string} value
 * @param {number} maxAgeSec
 */
function setCookie(name, value, maxAgeSec) {
  try {
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}` +
      `; Path=/; Max-Age=${maxAgeSec}; SameSite=Lax${secure}`;
  } catch (_e) {
    // ignore cookie failures (e.g. blocked storage)
  }
}

/**
 * @param {string} name
 */
function clearCookie(name) {
  try {
    const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${encodeURIComponent(name)}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
  } catch (_e) {
    // ignore
  }
}

/**
 * Set login-hop nudge flag. Prefer cookie — Android WebView often drops sessionStorage across navigation.
 * @param {string} value 'pending' or 'skip'
 */
export function setPasskeyNudgeLoginFlag(value) {
  setCookie(PASSKEY_NUDGE_LOGIN_FLAG_KEY, value, PASSKEY_NUDGE_PENDING_COOKIE_MAX_AGE_SEC);
  try {
    sessionStorage.setItem(PASSKEY_NUDGE_LOGIN_FLAG_KEY, value);
  } catch (_e) {
    // ignore — cookie is the CabView-safe path
  }
}

/**
 * Read login-hop flag without clearing (refresh can re-show while pending).
 * Cookie first — sessionStorage is unreliable in Android WebView after navigation.
 * @return {string|null}
 */
export function readPasskeyNudgeLoginFlag() {
  const fromCookie = getCookie(PASSKEY_NUDGE_LOGIN_FLAG_KEY);
  if (fromCookie === 'pending' || fromCookie === 'skip') {
    return fromCookie;
  }
  try {
    const fromSession = sessionStorage.getItem(PASSKEY_NUDGE_LOGIN_FLAG_KEY);
    if (fromSession === 'pending' || fromSession === 'skip') {
      return fromSession;
    }
  } catch (_e) {
    // ignore
  }
  return fromCookie || null;
}

/**
 * Clear pending/skip after explicit Not now, enroll, passkey login, or local key detected.
 * Showing the nudge must not clear this — CabView reloads need pending to re-prompt.
 */
export function clearPasskeyNudgeLoginFlag() {
  clearCookie(PASSKEY_NUDGE_LOGIN_FLAG_KEY);
  try {
    sessionStorage.removeItem(PASSKEY_NUDGE_LOGIN_FLAG_KEY);
  } catch (_e) {
    // ignore
  }
}

/**
 * True when the current document can run WebAuthn (browser or CabView WebView with platform support).
 * @return {boolean}
 */
export function isWebAuthnAvailable() {
  try {
    return browserSupportsWebAuthn();
  } catch (_e) {
    return false;
  }
}

/**
 * True when a platform authenticator (Touch ID / Face ID / Windows Hello / etc.) is available.
 * @return {Promise<boolean>}
 */
export async function isPlatformAuthenticatorAvailable() {
  try {
    return await platformAuthenticatorIsAvailable();
  } catch (_e) {
    return false;
  }
}

/**
 * Cookie name for explicit Not now snooze of the passkey nudge for this account.
 * @param {string|number} uid
 * @return {string}
 */
function getPasskeyNudgeDismissedKey(uid) {
  return `ic_passkey_nudge_dismissed_${uid}`;
}

/**
 * Cookie name for "this browser already has/used a passkey" for this account.
 * @param {string|number} uid
 * @return {string}
 */
function getPasskeyNudgeEnrolledKey(uid) {
  return `ic_passkey_nudge_enrolled_${uid}`;
}

/**
 * @param {string|number} uid
 * @return {boolean}
 */
export function isPasskeyNudgeDismissed(uid) {
  if (uid == null) {
    return false;
  }
  return getCookie(getPasskeyNudgeDismissedKey(uid)) === '1';
}

/**
 * Snooze the nudge for this browser + account after explicit Not now
 * (cookie; works in CabView WebView).
 * @param {string|number} uid
 */
export function setPasskeyNudgeDismissed(uid) {
  if (uid == null) {
    return;
  }
  setCookie(getPasskeyNudgeDismissedKey(uid), '1', PASSKEY_NUDGE_DISMISS_SNOOZE_SEC);
}

/**
 * @param {string|number} uid
 * @return {boolean}
 */
export function isPasskeyNudgeEnrolled(uid) {
  if (uid == null) {
    return false;
  }
  return getCookie(getPasskeyNudgeEnrolledKey(uid)) === '1';
}

/**
 * Mark that this browser already has / used a passkey for this account (cookie).
 * @param {string|number} uid
 */
export function setPasskeyNudgeEnrolled(uid) {
  if (uid == null) {
    return;
  }
  setCookie(getPasskeyNudgeEnrolledKey(uid), '1', PASSKEY_NUDGE_ENROLLED_COOKIE_MAX_AGE_SEC);
}

/**
 * Drop the enrolled marker (e.g. last passkey removed in Personal Settings)
 * so a later password login can show the nudge again.
 * @param {string|number} uid
 */
export function clearPasskeyNudgeEnrolled(uid) {
  if (uid == null) {
    return;
  }
  clearCookie(getPasskeyNudgeEnrolledKey(uid));
}

/**
 * This browser has a passkey for this account (created, already on the authenticator,
 * or used to log in). Suppresses the post-login nudge.
 * @param {string|number} uid
 */
export function markPasskeyPresentOnThisDevice(uid) {
  setPasskeyNudgeEnrolled(uid);
  clearPasskeyNudgeLoginFlag();
}

/**
 * @param {string} [ua]
 * @return {'iOS'|'Android'|'Desktop'}
 */
export function getPasskeyPlatformLabel(ua) {
  const agent = ua || (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '');
  if (/iPhone|iPad|iPod/i.test(agent) || (/Macintosh/i.test(agent) && navigator?.maxTouchPoints > 1)) {
    return 'iOS';
  }
  if (/Android/i.test(agent)) {
    return 'Android';
  }
  return 'Desktop';
}

/**
 * Linux desktops have WebAuthn APIs but usually no platform authenticator
 * (no Windows Hello / Touch ID equivalent). Chrome still reports create() as
 * NotAllowedError — the same name as a user cancelling the OS prompt.
 * @return {boolean}
 */
function isLinuxDesktop() {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const uaDataPlatform = navigator.userAgentData?.platform;
  if (uaDataPlatform) {
    return uaDataPlatform === 'Linux';
  }
  const ua = navigator.userAgent || '';
  if (/Android/i.test(ua)) {
    return false;
  }
  return /Linux/i.test(navigator.platform || '') || /Linux/i.test(ua);
}

/**
 * If create() rejects this fast, the OS prompt likely never appeared.
 * Used to tell "no authenticator" apart from a user dismissing Windows Hello / Face ID.
 */
const WEBAUTHN_CREATE_NO_PROMPT_MS = 400;

/**
 * @param {Error|DOMException} error
 * @param {string} expected
 * @return {boolean}
 */
function errorHasName(error, expected) {
  return error?.name === expected || error?.cause?.name === expected;
}

/**
 * Map a WebAuthn create() exception to a result error code.
 * @param {Error|DOMException} error
 * @param {object} [options] registration optionsJSON (for excludeCredentials)
 * @param {number} [elapsedMs]
 * @return {'already_registered'|'cancelled'|'create_failed'}
 */
function mapPasskeyCreateError(error, options, elapsedMs) {
  const message = String(error?.message || error?.cause?.message || '');

  if (errorHasName(error, 'InvalidStateError') || /already registered|already exists/i.test(message)) {
    const exclude = options?.excludeCredentials;
    const serverKnowsAKey = Array.isArray(exclude) && exclude.length > 0;
    return serverKnowsAKey ? 'already_registered' : 'create_failed';
  }

  if (errorHasName(error, 'AbortError')) {
    return 'cancelled';
  }

  // Spec uses NotAllowedError for both user cancel AND "no matching authenticator".
  // Linux (and instant rejects elsewhere) are real failures — show the error.
  if (errorHasName(error, 'NotAllowedError')) {
    const instantFail = typeof elapsedMs === 'number' && elapsedMs < WEBAUTHN_CREATE_NO_PROMPT_MS;
    if (isLinuxDesktop() || instantFail) {
      return 'create_failed';
    }
    return 'cancelled';
  }

  return 'create_failed';
}

/**
 * Unique default nickname for this platform, e.g. "Android passkey", "Android passkey (1)".
 * @param {string[]} existingNicknames
 * @param {string} [ua]
 * @return {string}
 */
export function getDefaultPasskeyNickname(existingNicknames, ua) {
  const base = `${getPasskeyPlatformLabel(ua)} passkey`;
  const used = new Set(
    (existingNicknames || [])
      .filter((n) => {
        return typeof n === 'string' && n.trim();
      })
      .map((n) => {
        return n.trim().toLowerCase();
      })
  );
  if (!used.has(base.toLowerCase())) {
    return base;
  }
  let i = 1;
  while (used.has(`${base} (${i})`.toLowerCase())) {
    i += 1;
  }
  return `${base} (${i})`;
}

/**
 * Usernameless passkey login (OS may show multi-account picker).
 * Same in-page ceremony in desktop browsers and CabView WebView.
 * @return {Promise<object>}
 */
export async function loginWithPasskey() {
  const optionsResp = await fetch('/webauthn/login/options', {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: '{}',
    cache: 'no-store',
  });
  if (!optionsResp.ok) {
    return {success: false, error: 'options_failed'};
  }
  const options = await optionsResp.json();
  if (options.error || options.success === false) {
    return {success: false, error: options.error || 'options_failed'};
  }

  const assertion = await startAuthentication({optionsJSON: options});
  const verifyResp = await fetch('/webauthn/login/verify', {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(assertion),
    cache: 'no-store',
  });
  let result;
  try {
    result = await verifyResp.json();
  } catch (_e) {
    return {success: false, error: 'verification_failed'};
  }
  if (!verifyResp.ok || result.success === false) {
    return {success: false, error: result.error || 'verification_failed'};
  }
  return result;
}

/**
 * Enroll a platform passkey for the logged-in user.
 * @param {object} args
 * @param {string} args.sessionSyncUserName
 * @param {string} args.sessionSyncDb
 * @param {string} [args.nickname]
 * @param {Function} args.fetchPostAuthSafe
 * @return {Promise<object>}
 */
export async function registerPasskey({sessionSyncUserName, sessionSyncDb, nickname, fetchPostAuthSafe}) {
  const optionsResp = await fetchPostAuthSafe(
    '/webauthn/register/options',
    {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: '{}',
      cache: 'no-store',
    },
    sessionSyncUserName,
    sessionSyncDb
  );
  if (!optionsResp.ok) {
    return {success: false, error: 'options_failed'};
  }
  const options = await optionsResp.json();
  if (options.error || options.success === false) {
    return {success: false, error: options.error || 'options_failed'};
  }

  const createStarted = Date.now();
  let attestation;
  try {
    attestation = await startRegistration({optionsJSON: options});
  } catch (error) {
    const mapped = mapPasskeyCreateError(error, options, Date.now() - createStarted);
    if (mapped !== 'cancelled') {
      console.error('Passkey create failed before verify:', error);
    }
    return {success: false, error: mapped};
  }

  const verifyResp = await fetchPostAuthSafe(
    '/webauthn/register/verify',
    {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({credential: attestation, nickname: nickname || undefined}),
      cache: 'no-store',
    },
    sessionSyncUserName,
    sessionSyncDb
  );
  let result;
  try {
    result = await verifyResp.json();
  } catch (_e) {
    return {success: false, error: 'verification_failed'};
  }
  if (!verifyResp.ok || result.success === false) {
    return {success: false, error: result.error || 'verification_failed'};
  }
  return result;
}

/**
 * @param {object} args
 * @param {string} args.sessionSyncUserName
 * @param {string} args.sessionSyncDb
 * @param {Function} args.fetchPostAuthSafe
 * @return {Promise<object>}
 */
export async function listPasskeys({sessionSyncUserName, sessionSyncDb, fetchPostAuthSafe}) {
  const response = await fetchPostAuthSafe(
    '/webauthn/credentials',
    {
      method: 'GET',
      headers: {'Content-Type': 'application/json'},
      cache: 'no-store',
    },
    sessionSyncUserName,
    sessionSyncDb
  );
  if (!response.ok) {
    return {success: false, error: 'list_failed'};
  }
  return await response.json();
}

/**
 * @param {object} args
 * @param {number} args.uid
 * @param {string} args.sessionSyncUserName
 * @param {string} args.sessionSyncDb
 * @param {Function} args.fetchPostAuthSafe
 * @return {Promise<object>}
 */
export async function deletePasskey({uid, sessionSyncUserName, sessionSyncDb, fetchPostAuthSafe}) {
  const response = await fetchPostAuthSafe(
    '/webauthn/deleteCredential',
    {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({uid}),
      cache: 'no-store',
    },
    sessionSyncUserName,
    sessionSyncDb
  );
  return await response.json();
}

/**
 * @param {object} args
 * @param {number} args.uid
 * @param {string} args.nickname
 * @param {string} args.sessionSyncUserName
 * @param {string} args.sessionSyncDb
 * @param {Function} args.fetchPostAuthSafe
 * @return {Promise<object>}
 */
export async function renamePasskey({uid, nickname, sessionSyncUserName, sessionSyncDb, fetchPostAuthSafe}) {
  const response = await fetchPostAuthSafe(
    '/webauthn/updateCredential',
    {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({uid, nickname}),
      cache: 'no-store',
    },
    sessionSyncUserName,
    sessionSyncDb
  );
  return await response.json();
}
